import type { OrderStorageResponse } from '@nebalkon-workspace/order-api';
import { parseQrToBoxOrItemId } from './qr-storage-entity.util';

export type OrderReceiveParsed =
  | { box_id: string }
  | { item_id: string };

export type ResolveOrderReceiveCodeFailureReason =
  | 'empty'
  | 'invalid'
  | 'too_short'
  | 'not_in_order'
  | 'ambiguous';

export type ResolveOrderReceiveCodeResult =
  | { ok: true; parsed: OrderReceiveParsed }
  | { ok: false; reason: ResolveOrderReceiveCodeFailureReason };

const HEX_SUFFIX_RE = /^[0-9a-f]+$/i;
const MIN_SUFFIX_LENGTH = 8;

function normalizeHexInput(raw: string): string {
  return raw.trim().replace(/-/g, '').toLowerCase();
}

function suffixFromHexInput(hex: string): string | null {
  if (!HEX_SUFFIX_RE.test(hex) || hex.length < MIN_SUFFIX_LENGTH) {
    return null;
  }
  return hex.slice(-MIN_SUFFIX_LENGTH);
}

function collectOrderReceiveEntities(
  storage: OrderStorageResponse,
): OrderReceiveParsed[] {
  const entities: OrderReceiveParsed[] = [];
  const seenBoxes = new Set<string>();
  const seenItems = new Set<string>();

  for (const line of storage.lines) {
    if (line.box_id) {
      if (!seenBoxes.has(line.box_id)) {
        seenBoxes.add(line.box_id);
        entities.push({ box_id: line.box_id });
      }
      continue;
    }
    if (line.item_id && !seenItems.has(line.item_id)) {
      seenItems.add(line.item_id);
      entities.push({ item_id: line.item_id });
    }
  }

  for (const box of storage.boxes) {
    if (!seenBoxes.has(box.box_id)) {
      seenBoxes.add(box.box_id);
      entities.push({ box_id: box.box_id });
    }
  }

  return entities;
}

function entityId(parsed: OrderReceiveParsed): string {
  return 'box_id' in parsed ? parsed.box_id : parsed.item_id;
}

function refineParsedWithOrderStorage(
  parsed: OrderReceiveParsed,
  storage: OrderStorageResponse | null,
): OrderReceiveParsed | null {
  if (!storage) {
    return null;
  }
  const id = entityId(parsed);
  for (const entity of collectOrderReceiveEntities(storage)) {
    if (entityId(entity) === id) {
      return entity;
    }
  }
  return null;
}

function resolveBySuffix(
  suffix: string,
  storage: OrderStorageResponse | null,
): ResolveOrderReceiveCodeResult {
  if (!storage) {
    return { ok: false, reason: 'not_in_order' };
  }

  const normalizedSuffix = suffix.toLowerCase();
  const matches = collectOrderReceiveEntities(storage).filter((entity) =>
    entityId(entity).replace(/-/g, '').toLowerCase().endsWith(normalizedSuffix),
  );

  if (matches.length === 1) {
    return { ok: true, parsed: matches[0] };
  }
  if (matches.length > 1) {
    return { ok: false, reason: 'ambiguous' };
  }
  return { ok: false, reason: 'not_in_order' };
}

/** Resolve QR payload, full UUID, or last-8-hex suffix within an order's storage. */
export function resolveOrderReceiveCode(
  raw: string,
  storage: OrderStorageResponse | null,
): ResolveOrderReceiveCodeResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, reason: 'empty' };
  }

  const parsedFromQr = parseQrToBoxOrItemId(trimmed);
  if (parsedFromQr) {
    if ('user_id' in parsedFromQr || 'cell_id' in parsedFromQr) {
      return { ok: false, reason: 'not_in_order' };
    }
    const refined = refineParsedWithOrderStorage(parsedFromQr, storage);
    if (refined) {
      return { ok: true, parsed: refined };
    }
    return { ok: false, reason: 'not_in_order' };
  }

  const hex = normalizeHexInput(trimmed);
  const suffix = suffixFromHexInput(hex);
  if (!suffix) {
    if (hex.length > 0 && hex.length < MIN_SUFFIX_LENGTH) {
      return { ok: false, reason: 'too_short' };
    }
    return { ok: false, reason: 'invalid' };
  }

  return resolveBySuffix(suffix, storage);
}
